import { useContext, useEffect } from "react";
import Page, { PageDOMProps } from "../page";
import { useIntl } from "react-intl";
import Table, { TableColumn } from "../../table";
import { Dropdown } from "react-bootstrap";
import { OrganizationRole } from "../../../model/OrganizationRole";
import ViewRoleModal from "../../modals/view-role-modal";
import TooltipTrigger from "../../tooltip-trigger";
import UIConfiguration from "../../../ui-configuration/configuration-provider";
import RoleTypeBadge from "../../badges/role-type-badge";
import { FeedbackContainerProps } from "../../feedback/with-feedback";
import ShowFeedback from "../../show-feedback";
import { OrganizationRoleLabels } from "../../../localization/organization-role";
import { resolveColumns } from "../../../ui-configuration/configuration-tools";
import RemoveRoleModal from "../../modals/remove-role-modal";
import CreateOrganizationRoleModal from "../../modals/create-organization-role-modal";
import ManageRoleMembersModal from "../../modals/manage-role-members-modal";

export enum ModalKeys {
  show = "show",
  edit = "edit",
  remove = "remove",
  members = "members",
  create = "create",
}
//<editor-fold desc="Props">

export interface RolesModalVisibilityProps {
  showModal?: { key: ModalKeys; roleId?: string; secondaryId?: string };
  onShowModal: (key: ModalKeys, roleId?: string, secondaryId?: string) => void;
  onHideModal: (callback?: () => void) => void;
}
export interface RolesDOMProps extends PageDOMProps {}

export interface RolesStateProps extends FeedbackContainerProps {
  selected: OrganizationRole[];
  onSetSelected: (selection: OrganizationRole[]) => void;
}
export interface RolesProps
  extends RolesStateProps,
    RolesDOMProps,
    RolesModalVisibilityProps {
  userName?: string;
  userId?: string;
  roles?: OrganizationRole[];
  onLoadRoles: () => Promise<any>;
  orgAdminRoleId?: string;
}
//</editor-fold>

//<editor-fold desc="Messages">
//</editor-fold>

//<editor-fold desc="Utils">

const resolveRowClasses = (row: OrganizationRole, rowIndex: number) => {
  /*
  const t: string[] = [];
  return t.length ? t.join(" ") : undefined;
   */
  return undefined;
};

const resolveRole = (
  roleId: string | undefined,
  roles: OrganizationRole[] | undefined
) => {
  let retVal: OrganizationRole | undefined | null;
  if (roleId && roles) {
    retVal = roles.find((val) => val.id === roleId);
    if (!retVal) {
      retVal = null;
    }
  }
  return retVal;
};

//</editor-fold>

function Roles(props: RolesProps) {
  //<editor-fold desc="Local variables">
  const {
    roles,
    onLoadRoles,
    selected,
    onSetSelected,
    onShowModal,
    onHideModal,
    showModal,
    onShowFeedback,
    onHideFeedback,
    feedback,
    userName,
    userId,
    orgAdminRoleId,
    ...others
  } = props;

  const modalRole: OrganizationRole | undefined | null = showModal
    ? resolveRole(showModal.roleId, roles)
    : undefined;

  // this is more like a variable than a hook
  const intl = useIntl();
  const title = intl.formatMessage({
    defaultMessage: "Manage roles",
    description: "heading for the Manage roles page",
  });
  const description = intl.formatMessage({
    defaultMessage: "Organization role management",
    description: "window meta description for the Manage roles screen",
  });

  const { conf } = useContext(UIConfiguration);
  const roleConf =
    conf.functionality && conf.functionality.roles
      ? conf.functionality.roles
      : {};

  let columns: TableColumn[] = [
    {
      key: "id",
      label: intl.formatMessage(OrganizationRoleLabels.id),
      isTechnical: true,
      hidden: true,
    },
    {
      key: "type",
      sortable: true,
      label: intl.formatMessage(OrganizationRoleLabels.type),
      renderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        return <RoleTypeBadge type={props.cell} withLabel={true} />;
      },
      tipRenderer: () => false,
    },
    {
      key: "name",
      sortable: true,
      label: intl.formatMessage(OrganizationRoleLabels.name),
    },
    {
      key: "description",
      sortable: true,
      label: intl.formatMessage(OrganizationRoleLabels.description),
    },
  ];
  if (roleConf.columns && roleConf.columns.length > 0) {
    columns = resolveColumns(columns, roleConf.columns);
  }
  //</editor-fold>

  //<editor-fold desc="Hooks">

  // resets selection when users are undefined, and clears out selected items that are not part of updated data
  useEffect(() => {
    if (selected.length) {
      if (roles === undefined) {
        onSetSelected([]);
      } else {
        const ids = roles.map((grp: OrganizationRole) => grp.id);
        const newS = selected.filter((itm: any) => {
          return ids.indexOf(itm.id) >= 0;
        });
        if (newS.length !== selected.length) {
          onSetSelected(newS);
        }
      }
    }
  }, [roles, selected, onSetSelected]);
  //</editor-fold>
  return (
    <Page
      header={<h1>{title}</h1>}
      data-test-roles-page
      id={"roles-page"}
      meta={{
        title,
        description,
      }}
      {...others}
    >
      <ShowFeedback
        idPrefix={"roles_"}
        onHideFeedback={onHideFeedback}
        feedback={feedback}
      />
      <Table<OrganizationRole>
        data-test-roles-table
        persistStateKey={"roles-table"}
        allowColumnSort={true}
        selection={{
          multi: false,
          selectAll: false,
        }}
        header={
          roleConf.create && (
            <button
              data-test-create-organization-role-trigger
              onClick={(e: any) => {
                e.preventDefault();
                e.stopPropagation();
                onShowModal(ModalKeys.create);
              }}
              className={"btn btn-theme-to-primary mr-auto"}
            >
              {intl.formatMessage({
                defaultMessage: "Create",
                description: "create role button label",
              })}
            </button>
          )
        }
        search={true}
        rowClasses={resolveRowClasses}
        selected={selected}
        onSelectionChanged={onSetSelected}
        columns={columns}
        columnToggle
        reload={true}
        onLoadData={onLoadRoles}
        data={roles ? roles : undefined}
        identifyingColumn={"id"}
        pagination={false}
        rowTools={
          roleConf.show ||
          roleConf.members ||
          roleConf.remove ||
          roleConf.invite
            ? (props: { rowEntry: OrganizationRole }) => {
                return (
                  <>
                    {roleConf.show && (
                      <Dropdown.Item
                        data-test-row-tool-role-details={props.rowEntry.id}
                        onClick={(e: any) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onShowModal(ModalKeys.show, props.rowEntry.id);
                        }}
                      >
                        {intl.formatMessage({
                          defaultMessage: "View details",
                          description: "Label for the tool",
                        })}
                      </Dropdown.Item>
                    )}
                    {roleConf.members && (
                      <Dropdown.Item
                        data-test-row-tool-role-members={props.rowEntry.id}
                        onClick={(e: any) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onShowModal(ModalKeys.members, props.rowEntry.id);
                        }}
                      >
                        {intl.formatMessage({
                          defaultMessage: "Members",
                          description: "Label for the tool",
                        })}
                      </Dropdown.Item>
                    )}
                    {roleConf.remove && props.rowEntry.id === orgAdminRoleId && (
                      <TooltipTrigger
                        tip={intl.formatMessage({
                          defaultMessage: 'The "admin" role cannot be removed.',
                          description:
                            "tooltip for disabled admin role removal",
                        })}
                        tipKey={"removeBlockedTip"}
                        placement={"auto"}
                        wrapDisabled={true}
                        className={"d-block"}
                      >
                        <Dropdown.Item
                          data-test-row-tool-remove-role={props.rowEntry.id}
                          onClick={(e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onShowModal(ModalKeys.remove, props.rowEntry.id);
                          }}
                          disabled={true}
                        >
                          {intl.formatMessage({
                            defaultMessage: "Remove",
                            description: "Label for the tool",
                          })}
                        </Dropdown.Item>
                      </TooltipTrigger>
                    )}
                    {roleConf.remove && props.rowEntry.id !== orgAdminRoleId && (
                      <Dropdown.Item
                        data-test-row-tool-remove-role={props.rowEntry.id}
                        onClick={(e: any) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onShowModal(ModalKeys.remove, props.rowEntry.id);
                        }}
                      >
                        {intl.formatMessage({
                          defaultMessage: "Remove",
                          description: "Label for the tool",
                        })}
                      </Dropdown.Item>
                    )}
                  </>
                );
              }
            : undefined
        }
      />
      {roleConf.show && (
        <ViewRoleModal
          show={
            showModal && showModal.roleId
              ? (roleConf.show && showModal.key === ModalKeys.show) ||
                (roleConf.edit && showModal.key === ModalKeys.edit)
                ? true
                : false
              : false
          }
          onShowFeedback={onShowFeedback}
          onClose={onHideModal}
          edit={showModal ? showModal.key === ModalKeys.edit : false}
          allowEdit={roleConf.edit ? true : false}
          roleId={showModal ? showModal.roleId : undefined}
        />
      )}
      {roleConf.remove && (
        <RemoveRoleModal
          show={
            showModal && showModal.roleId
              ? showModal.key === ModalKeys.remove
              : false
          }
          onShowFeedback={onShowFeedback}
          onClose={onHideModal}
          roleId={showModal ? showModal.roleId : undefined}
        />
      )}
      {roleConf.create && (
        <CreateOrganizationRoleModal
          show={showModal ? showModal.key === ModalKeys.create : false}
          onShowFeedback={onShowFeedback}
          onClose={onHideModal}
        />
      )}
      {roleConf.members && (
        <ManageRoleMembersModal
          show={
            showModal
              ? showModal.key === ModalKeys.members && modalRole !== undefined
              : false
          }
          onShowFeedback={onShowFeedback}
          onClose={onHideModal}
          roleId={showModal ? showModal.roleId : undefined}
        />
      )}
    </Page>
  );
}
export default Roles;
